import { useEffect, useRef, useState } from 'react';
import { COLORS, INPUT_STYLE } from '../../constants/theme';

interface AddTierModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The Div number the new tier would receive — always one below the current lowest tier. */
  newDivRank: number;
  universeName: string;
}

/**
 * Single-step Add Tier modal (SF-103). No age-naming step — a tier added to an existing
 * universe joins whatever age is already current.
 */
export function AddTierModal({ isOpen, onClose, newDivRank, universeName }: AddTierModalProps) {
  const [worldName, setWorldName] = useState('');
  const [swapPct, setSwapPct] = useState('10');
  const [showError, setShowError] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const worldInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<Element | null>(null);

  const trimmedWorldName = worldName.trim();
  const parsedSwapPct = Number(swapPct);
  const isNameValid = trimmedWorldName.length > 0;
  const isSwapPctValid = Number.isFinite(parsedSwapPct) && parsedSwapPct > 0 && parsedSwapPct <= 100;

  useEffect(() => {
    if (!isOpen) return;

    triggerRef.current = document.activeElement;
    setWorldName('');
    setSwapPct('10');
    setShowError(false);
    worldInputRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'input, button, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleAddTier = () => {
    if (!isNameValid || !isSwapPctValid) {
      setShowError(true);
      if (!isNameValid) worldInputRef.current?.focus();
      return;
    }
    setShowError(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-tier-title"
        className="rounded-lg shadow-xl max-w-md w-full mx-4"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
          <span id="add-tier-title" className="text-base font-semibold" style={{ color: COLORS.text.primary }}>
            Add Tier
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ color: COLORS.text.tertiary, background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}
          >
            ×
          </button>
        </div>

        <div className="p-6">
          <label htmlFor="tier-world-name" className="block mb-1.5 text-sm" style={{ color: COLORS.text.tertiary }}>
            World name
          </label>
          <input
            ref={worldInputRef}
            id="tier-world-name"
            type="text"
            placeholder="e.g. Wraithbound Reach"
            autoComplete="off"
            value={worldName}
            onChange={(e) => setWorldName(e.target.value)}
            className="w-full px-3 py-2 rounded focus:outline-none"
            style={INPUT_STYLE}
          />
          {showError && !isNameValid && (
            <p className="mt-1 text-xs" style={{ color: COLORS.negative }}>World name is required.</p>
          )}

          <label htmlFor="tier-swap-pct" className="block mb-1.5 text-sm mt-4" style={{ color: COLORS.text.tertiary }}>
            Swap with the tier above
          </label>
          <input
            id="tier-swap-pct"
            type="text"
            placeholder="e.g. 10"
            autoComplete="off"
            value={swapPct}
            onChange={(e) => setSwapPct(e.target.value)}
            className="w-full px-3 py-2 rounded focus:outline-none"
            style={INPUT_STYLE}
          />
          {showError && !isSwapPctValid && (
            <p className="mt-1 text-xs" style={{ color: COLORS.negative }}>Enter a swap percentage between 1 and 100.</p>
          )}
          <p className="mt-1.5 text-xs" style={{ color: COLORS.text.tertiary }}>
            % of kingdoms that swap between this tier and the one above it at each age boundary.
            Can be changed later from the ladder.
          </p>

          <div
            className="mt-4 px-4 py-3 rounded text-sm"
            style={{ backgroundColor: COLORS.cardAlt, border: `1px solid ${COLORS.border}`, color: COLORS.text.tertiary }}
          >
            This becomes <span style={{ color: COLORS.text.primary, fontFamily: 'monospace' }}>Div {newDivRank}</span> —
            the new lowest tier in {universeName}. It joins the universe's current age automatically; no separate age
            setup needed.
          </div>
        </div>

        <div className="flex gap-3 justify-end px-6 py-4" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded text-sm"
            style={{ backgroundColor: COLORS.cardAlt, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAddTier}
            className="px-4 py-2 rounded text-sm font-medium"
            style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary }}
          >
            Add Tier
          </button>
        </div>
      </div>
    </div>
  );
}
